import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {KTCard, KTCardBody, KTIcon} from '../../../_metronic/helpers'
import {useAuth} from '../../modules/auth'
import {checkError} from '../../modules/globallycommanFunctions/commanfunction'
import {DashboardMeta} from './dashboardTableTypes'
import {PageTitle, PageLink} from '../../../_metronic/layout/core'
import {useNavigate} from 'react-router-dom'
import {Table, TableColumn} from '../../../ui/Table'
import {SearchBar} from '../../../ui/SearchBar'
import {SingleSelectedButton, SingleSelectOption} from '../../../ui/SingleSelectedButton'

const dashboardBreadcrumbs: Array<PageLink> = [
  {title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false},
  {title: '', path: '', isSeparator: true, isActive: false},
]

export interface DashboardColumn<T> {
  header: React.ReactNode
  render: (item: T, index: number) => React.ReactNode
  className?: string
  headerClassName?: string
  sortKey?: string
  /** Fixed px width; omit for a flexible column (see TableColumn.width). */
  width?: number | string
  /** Smallest width a flexible column shrinks to before the table scrolls. */
  minWidth?: number
}

export interface DashboardFilter {
  /** Query param name sent to the API. */
  param: string
  options: SingleSelectOption[]
  /** Control and menu width in px. Defaults to 200. */
  width?: number
}

interface DashboardApiTableShowProps<T> {
  /** Optional dropdown filter; the selected option's value is sent as `param` (omitted when empty). */
  filter?: DashboardFilter
  endpoint: string
  title: string
  columns: DashboardColumn<T>[]
  /** Overrides the user_type_id param, which defaults to currentUser.user_type.id. */
  userTypeId?: number | string | null
  /** Label in the pagination's "Showing 1-10 of 42 {paginationTitle}". Defaults to `title`. */
  paginationTitle?: string
  /** Extra toolbar content (left of Back); receives the raw `data` object of the list response. */
  renderToolbarExtras?: (responseData: any) => React.ReactNode
}

function DashboardApiTableShow<T = any>({
  endpoint,
  title,
  columns,
  userTypeId: userTypeIdOverride,
  paginationTitle,
  renderToolbarExtras,
  filter,
}: DashboardApiTableShowProps<T>) {
  const API_URL = process.env.REACT_APP_KT_API_URL
  const tokenString: any = localStorage.getItem('kt-auth-react-v')
  const token = JSON.parse(tokenString)
  const {currentUser, logout} = useAuth()
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<T[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total_pages, setTotal_pages] = useState<DashboardMeta['totalPages'] | null>(null)
  const [total_items, setTotal_Items] = useState<DashboardMeta['totalItems']>(0)
  const [lorder, setLorder] = useState(false)
  const [responseData, setResponseData] = useState<any>(null)

  const [search, setSearch] = useState('')
  const [filterValue, setFilterValue] = useState('')

  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined)

  const toastError = (errorMessage) => {
    toast.error(errorMessage, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
    })
  }

  useEffect(() => {
    if (total_pages && currentPage > total_pages) {
      setCurrentPage(total_pages)
    }
  }, [currentPage, total_pages])

  const fetchData = async () => {
    setLorder(true)
    try {
      const params = new URLSearchParams()
      const userTypeId = userTypeIdOverride ?? currentUser?.user_type?.id
      const userId = currentUser?.id
      if (userTypeId !== null && userTypeId !== undefined) {
        params.append('user_type_id', userTypeId.toString())
      }
      if (userId !== null && userId !== undefined) {
        params.append('userId', userId.toString())
      }
      if (search !== '') {
        params.append('search', search)
      }
      if (sortField && sortOrder) {
        params.append('sort', sortField)
        params.append('order', sortOrder)
      }
      if (filter && filterValue !== '') {
        params.append(filter.param, filterValue)
      }
      params.append('page', currentPage.toString())
      params.append('limit', pageSize.toString())

      const response = await fetch(`${API_URL}/dashboard/${endpoint}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token?.access_token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await response.json()
      if (!response.ok) {
        setLorder(false)
        if (response?.status === 401) {
          checkError(response?.status, logout)
        } else {
          toastError(data?.message)
        }
        return
      }
      setResponseData(data?.data ?? null)
      setTableData(data?.data?.items ?? [])
      setTotal_pages(data?.data?.meta?.totalPages)
      setTotal_Items(data?.data?.meta?.totalItems ?? 0)
      setLorder(false)
    } catch (error) {
      setLorder(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterValue, currentPage, pageSize, sortField, sortOrder])

  const handleSearchChange = (value: string) => {
    setSearch(value.trim())
    setCurrentPage(1)
  }

  const handleSort = (sortKey?: string) => {
    if (!sortKey) return
    if (sortField !== sortKey) {
      setSortField(sortKey)
      setSortOrder('asc')
    } else if (sortOrder === 'asc') {
      setSortOrder('desc')
    } else {
      setSortField(undefined)
      setSortOrder(undefined)
    }
    setCurrentPage(1)
  }

  const renderHeader = (col: DashboardColumn<T>) => {
    if (!col.sortKey) return col.header
    const isSorted = sortField === col.sortKey && !!sortOrder
    return (
      <span
        role='button'
        className={`d-inline-flex align-items-center ${isSorted ? `table-sort-${sortOrder}` : 'table-sort'}`}
        onClick={() => handleSort(col.sortKey)}
      >
        {col.header}
      </span>
    )
  }

  const tableColumns: TableColumn<T>[] = [
    {
      id: 'sno',
      header: 'S.No',
      width: 70,
      cell: (_item, index) => pageSize * (currentPage - 1) + index + 1,
    },
    ...columns.map((col) => ({
      id: col.sortKey ?? String(col.header),
      header: renderHeader(col),
      cell: col.render,
      width: col.width,
      minWidth: col.minWidth,
      truncate: true,
      headerClassName: col.headerClassName,
      cellClassName: col.className,
    })),
  ]

  return (
    <React.Fragment>
      <PageTitle breadcrumbs={dashboardBreadcrumbs}>{title}</PageTitle>
      <KTCard>
        <div className='products-filter-bar d-flex align-items-center justify-content-between card-header border-0 pt-6 pb-4 gap-3'>
          <div className='products-filter-search'>
            <SearchBar value={search} onChange={handleSearchChange} width='100%' debounceMs={500} />
          </div>
          <div className='d-flex align-items-center gap-3'>
            {filter && (
              <SingleSelectedButton
                options={filter.options}
                value={filter.options.find((opt) => opt.value === filterValue) ?? null}
                onChange={(opt) => {
                  setFilterValue(opt?.value ?? '')
                  setCurrentPage(1)
                }}
                placeholder={filter.options[0]?.label}
                icon={<KTIcon iconName='filter' className='fs-3 ms-3 text-gray-600' />}
                widthClass={filter.width ?? 200}
                menuWidth={filter.width ?? 200}
                sortAlphabetically={false}
                showSearch={false}
                closeMenuOnSelect
              />
            )}
            {renderToolbarExtras?.(responseData)}
            <button type='button' className='btn btn-light-primary' onClick={() => navigate(-1)}>
              <KTIcon iconName='arrow-left' className='fs-2' />
              Back
            </button>
          </div>
        </div>

        <KTCardBody className='py-4 set_card_padding_right_left'>
          <Table
            columns={tableColumns}
            data={tableData}
            rowKey={(item) => (currentPage - 1) * pageSize + tableData.indexOf(item)}
            loading={lorder}
            pagination={
              tableData.length > 0
                ? {
                    page: currentPage,
                    limit: pageSize,
                    totalItems: total_items,
                    onPageChange: setCurrentPage,
                    onLimitChange: (nextLimit) => {
                      setPageSize(nextLimit)
                      setCurrentPage(1)
                    },
                    title: paginationTitle ?? title,
                  }
                : undefined
            }
          />
        </KTCardBody>
      </KTCard>
    </React.Fragment>
  )
}

export default DashboardApiTableShow
